import {useContext, useState} from 'react'

import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {checkConstraintsFactory, formBuilderChangeFieldById, formBuilderPickFieldById} from '@s-ui/react-form-builder'
import IconCheck from '@adv-mt/mt-svg-icons/lib/CheckSmall.js'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'

import InputAddonPlate from './InputAddonPlate.js'
import {
  getClearedNumericValue,
  getFieldDataById,
  getNextFieldValue,
  getRequestInterceptorHandler,
  isValidForm
} from './utils.js'

const iconArrowDown = <IconChevronDown aria-hidden="true" role="presentation" size={atomIconSizes.medium} />

const commonSelectProps = {
  autoComplete: 'off',
  iconArrowDown,
  selectSize: 'l',
  size: 'large'
}

const commonAutosuggestProps = {
  iconArrowDown,
  inputSize: 'l',
  size: 'large'
}

const NO_OP = () => {}

const useFormVehicle = ({
  makesList,
  onAutocompleteField,
  onClickSubmit,
  onFieldChange,
  onFormChange = NO_OP,
  onSubmitError,
  initialFieldValues
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')

  const {
    fieldsToTagOnBlurValue,
    fieldsToTagOnChangeValue,
    formFieldIds,
    formC2bVehicleFormConfig: {initialValues: defaultInitialFieldValues, fields: formFields, remotes, rules}
  } = config.get('form')

  const [fields, setFields] = useState({...defaultInitialFieldValues, ...initialFieldValues})
  const [formConfiguration, setFormConfiguration] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [validatedErrors, setValidatedErrors] = useState({})

  const {
    bodyTypeDoorsId: bodyTypeDoorsFieldId,
    fuelId: fuelFieldId,
    kilometersId: kilometersFieldId,
    makeId: makeFieldId,
    modelId: modelFieldId,
    plate: plateFieldId,
    transmissionTypeId: transmissionTypeFieldId,
    vehicleStatus: vehicleStatusFieldId,
    versionId: versionFieldId,
    yearId: yearFieldId
  } = formFieldIds

  if (!formConfiguration) {
    setFormConfiguration(() => {
      for (const field of formFields) {
        const {hint, id, label, help, constraints, datalist} = field
        if (hint) field.hint = i18n.t(hint, 1)
        if (label) field.label = i18n.t(label, 1)
        if (help) field.help = i18n.t(help, 1)
        if (constraints) {
          constraints.forEach(constraint => {
            if (constraint.message) constraint.message = i18n.t(constraint.message, 1)
          })
        }
        if (datalist) {
          datalist.forEach(datalistItem => {
            datalistItem.text = i18n.t(datalistItem.text, 1)
            if (datalistItem.ariaLabel) {
              datalistItem.ariaLabel = i18n.t(datalistItem.ariaLabel, 1)
            }
          })
        }
        if (id === makeFieldId) {
          field.datalist = makesList
        }
      }

      return {
        form: {
          id: 'mt-form-c2b-vehicle',
          type: 'group',
          fields: [
            {
              id: 'mt-form-c2b-vehicle-fields',
              type: 'fieldset',
              display: 'block',
              fields: formFields
            }
          ],
          rules
        }
      }
    })
  }

  // eslint-disable-next-line react/prop-types
  const SUIComponentsConfig = ({id}) => {
    const props = {
      [plateFieldId]: {leftAddon: <InputAddonPlate />, size: 'l'},
      [makeFieldId]: commonAutosuggestProps,
      [modelFieldId]: commonAutosuggestProps,
      [bodyTypeDoorsFieldId]: commonAutosuggestProps,
      [fuelFieldId]: commonSelectProps,
      [yearFieldId]: commonSelectProps,
      [versionFieldId]: commonAutosuggestProps,
      [transmissionTypeFieldId]: {
        fullWidth: true,
        selectedIcon: <IconCheck size={atomIconSizes.medium} />
      },
      [kilometersFieldId]: {
        autoComplete: 'off',
        inputMode: 'numeric',
        size: 'l'
      },
      [vehicleStatusFieldId]: commonSelectProps
    }
    return props[id]
  }

  const requestInterceptorHandler = ({fieldID, fields}) => {
    return getRequestInterceptorHandler({
      fieldID,
      fields,
      remotes,
      useCase: domain.get('get_specs_url_form_use_case')
    }).then(([, url]) => {
      const {TENANT} = config.get('REQUEST_HEADERS')
      const platform = config.get('TENANT')
      const headers = {[TENANT]: platform}
      return {headers, url}
    })
  }

  const responseInterceptorHandler = responseIntercepted => {
    const {
      response: {datalist = []}
    } = responseIntercepted

    return {
      datalist,
      disabled: false,
      hidden: false,
      value: '',
      ...(!datalist.length && {
        hint: i18n.t('PUBLISH_AD.FB.MODELS.NO_RESULTS')
      })
    }
  }

  const setErrors = errors =>
    setValidatedErrors(prevErrors => {
      const allErrors = {
        ...prevErrors,
        ...errors
      }

      return allErrors
    })

  const checkConstraints = ({fieldId} = {}) => {
    const checkParams = fieldId ? {for: fieldId} : {all: true}
    const _errors = checkConstraintsFactory(formConfiguration)(checkParams)

    setErrors(_errors)
    return _errors
  }

  const handleOnBlur = fieldId => {
    const {value} = formBuilderPickFieldById(formConfiguration?.form.fields, fieldId)

    if (fieldsToTagOnBlurValue.includes(fieldId)) {
      checkConstraints({fieldId})
      value && onFieldChange({fieldId})
    }
  }

  const buildFormSnapshot = newFields => {
    const makeName = getFieldDataById({formFields: formConfiguration.form.fields, fieldId: makeFieldId})?.valueText
    const modelName = getFieldDataById({formFields: formConfiguration.form.fields, fieldId: modelFieldId})?.valueText
    const versionName = getFieldDataById({
      formFields: formConfiguration.form.fields,
      fieldId: versionFieldId
    })?.valueText
    return {
      ...newFields,
      ...(makeName && {makeName}),
      ...(modelName && {modelName}),
      ...(versionName && {versionName})
    }
  }

  const handleOnChange = ({__AUTOCOMPLETED_FIELD__, __FIELD_CHANGED__, __FORM_STATE__, ...newFields}) => {
    const formFields = formConfiguration.form.fields

    if (__FIELD_CHANGED__ === kilometersFieldId) {
      const clearedValue = getClearedNumericValue({value: newFields[__FIELD_CHANGED__]})
      const formattedValue = clearedValue && i18n.n(Number(clearedValue))

      newFields[kilometersFieldId] = formattedValue
      formBuilderChangeFieldById(formFields, kilometersFieldId, {
        value: formattedValue
      })
    } else if (__FIELD_CHANGED__ === plateFieldId) {
      const upperCasedPlate = newFields[plateFieldId].toUpperCase()
      const formattedValue = upperCasedPlate.replace(/[^A-Z0-9]/g, '')

      newFields[plateFieldId] = formattedValue
      formBuilderChangeFieldById(formFields, plateFieldId, {
        value: formattedValue
      })
    } else {
      checkConstraints({fieldId: __FIELD_CHANGED__})
    }

    if (fieldsToTagOnChangeValue.includes(__FIELD_CHANGED__)) {
      const action = __AUTOCOMPLETED_FIELD__ ? onAutocompleteField : onFieldChange
      action({fieldId: __FIELD_CHANGED__})
    }

    setFields({...newFields})
    onFormChange({fields: buildFormSnapshot(newFields)})

    const nextFieldValue = getNextFieldValue({fields: formFields[0].fields, nextFieldId: __FIELD_CHANGED__})
    if (nextFieldValue) return nextFieldValue
  }

  const handleOnSubmit = () => {
    setIsLoading(true)

    const errors = checkConstraints()

    if (isValidForm(errors)) {
      const [makeName, modelName, versionName] = [makeFieldId, modelFieldId, versionFieldId].map(
        fieldId => getFieldDataById({formFields: formConfiguration?.form.fields, fieldId}).valueText
      )
      const {bodyTypeDoors, fuelId, km, makeId, modelId, plate, transmissionTypeId, vehicleStatus, versionId, year} =
        fields

      onClickSubmit({
        vehicle: {
          ...(bodyTypeDoors && {bodyTypeDoors}),
          ...(fuelId && {fuelId}),
          ...(km && {km: getClearedNumericValue({value: km})}),
          ...(makeId && {makeId}),
          makeName: makeName || fields.makeName,
          ...(modelId && {modelId}),
          modelName: modelName || fields.modelName,
          ...(plate && {plate}),
          ...(transmissionTypeId && {transmissionTypeId}),
          ...(vehicleStatus && {vehicleStatus}),
          ...(versionId && {versionId}),
          versionName: versionName || fields.versionName,
          ...(year && {year})
        }
      })
    } else {
      onSubmitError({errors})
      setIsLoading(false)
    }
  }

  const kmClearedInitialValue = getClearedNumericValue({value: initialFieldValues[kilometersFieldId]})
  const initialValues = {
    ...defaultInitialFieldValues,
    ...initialFieldValues,
    [kilometersFieldId]: kmClearedInitialValue && i18n.n(Number(kmClearedInitialValue))
  }

  return {
    // config
    formConfiguration,
    initialValues,
    SUIComponentsConfig,
    // state
    isLoading,
    validatedErrors,
    // handlers
    handleOnBlur,
    handleOnChange,
    handleOnSubmit,
    requestInterceptorHandler,
    responseInterceptorHandler,
    setValidatedErrors
  }
}

export default useFormVehicle
